import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import apiClient from '../utils/api';

export const EditShipment: React.FC<{ basePath: string }> = ({ basePath }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    receiverName: '', receiverPhone: '', receiverEmail: '',
    receiverAddress: '', receiverCity: '', priority: 'NORMAL',
    references: '', estimatedDeliveryTime: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.getShipment(id!).then(r => {
      const s = r.data.data;
      if (['DEPARTED', 'IN_TRANSIT', 'DELIVERED', 'CANCELLED'].includes(s.status)) {
        setError('This shipment can no longer be edited.');
        setLoading(false);
        return;
      }
      setForm({
        receiverName: s.receiverName ?? '',
        receiverPhone: s.receiverPhone ?? '',
        receiverEmail: s.receiverEmail ?? '',
        receiverAddress: s.receiverAddress ?? '',
        receiverCity: s.receiverCity ?? '',
        priority: s.priority ?? 'NORMAL',
        references: s.references ?? '',
        estimatedDeliveryTime: s.estimatedDeliveryTime
          ? new Date(s.estimatedDeliveryTime).toISOString().slice(0, 16)
          : '',
      });
    }).catch(() => setError('Failed to load shipment.')).finally(() => setLoading(false));
  }, [id]);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      const payload: any = { ...form };
      if (!payload.estimatedDeliveryTime) delete payload.estimatedDeliveryTime;
      await apiClient.updateShipment(id!, payload);
      navigate(`${basePath}/${id}`);
    } catch (err: any) {
      setError(err.response?.data?.error ?? 'Failed to update shipment.');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="loading-spinner">Loading...</div>;

  return (
    <>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost" onClick={() => navigate(-1)}><ArrowLeft size={16} /></button>
          <h1 className="page-title">Edit Shipment</h1>
        </div>
      </div>

      <div className="card" style={{ maxWidth: 700 }}>
        {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 6, marginBottom: 16, fontSize: 14 }}>{error}</div>}
        {!error && (
          <form onSubmit={submit}>
            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#64748b', marginBottom: 16, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Receiver Information</h3>
            <div className="form-grid">
              <div className="form-group"><label>Receiver Name *</label><input required value={form.receiverName} onChange={e => set('receiverName', e.target.value)} /></div>
              <div className="form-group"><label>Receiver Phone *</label><input required value={form.receiverPhone} onChange={e => set('receiverPhone', e.target.value)} /></div>
              <div className="form-group"><label>Receiver Email</label><input type="email" value={form.receiverEmail} onChange={e => set('receiverEmail', e.target.value)} /></div>
              <div className="form-group"><label>City</label><input value={form.receiverCity} onChange={e => set('receiverCity', e.target.value)} /></div>
              <div className="form-group full"><label>Address *</label><input required value={form.receiverAddress} onChange={e => set('receiverAddress', e.target.value)} /></div>
            </div>

            <h3 style={{ fontSize: 14, fontWeight: 600, color: '#64748b', margin: '20px 0 16px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Shipment Details</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>Priority</label>
                <select value={form.priority} onChange={e => set('priority', e.target.value)}>
                  {['LOW', 'NORMAL', 'HIGH', 'URGENT'].map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div className="form-group"><label>References</label><input value={form.references} onChange={e => set('references', e.target.value)} /></div>
              <div className="form-group"><label>Estimated Delivery</label><input type="datetime-local" value={form.estimatedDeliveryTime} onChange={e => set('estimatedDeliveryTime', e.target.value)} /></div>
            </div>

            <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
              <button type="submit" className="btn btn-primary" disabled={saving}><Save size={15} /> {saving ? 'Saving...' : 'Save Changes'}</button>
              <button type="button" className="btn btn-ghost" onClick={() => navigate(-1)}>Cancel</button>
            </div>
          </form>
        )}
      </div>
    </>
  );
};
